'use client';
import React from 'react';
import CustomImage from '@components/CustomImage';
import {
  DynamicTabsProps,
  getModuleClass,
  getAnimationClass,
  useDynamicTabs,
} from './DynamicTabs.logic';
import ReactHtmlParser from '@libs/reactHtmlParser';

import cn from './style.module.scss';

const DynamicTabs: React.FC<DynamicTabsProps> = ({
  tabs,
  wrapperClassName = '',
  className = '',
  tabClass = '',
  elementClass = '',
  listClassName = '',
  itemClassName = '',
  linkClass = '',
  tabIconClass = '',
  contentClass = '',
  contentBlockClass = '',
  activeClassName = '',
  bgFlowClass = '',
  showFlowIndicator = false,
  showIndenter = false,
  changeOnHover = false,
  triggerOnActiveClick = false,
  slideDirection = 'horizontal',
  moveDirection = undefined,
  layout = 'vertical',
  defaultActiveIndex = 0,
  onTabChange,
}) => {
  const { activeTab, direction, flowStyle, tabRefs, changeTab } =
    useDynamicTabs(
      defaultActiveIndex,
      showFlowIndicator,
      onTabChange,
      moveDirection,
      slideDirection
    );

  const getTabClassName = (index: number) => {
    const baseClass = cn.dynamicTabData;
    if (index === activeTab) {
      return `${baseClass} ${getAnimationClass(direction)}`;
    }
    return baseClass;
  };

  return (
    <div className={`${cn.dynamicTabsWrapper} ${wrapperClassName}`}>
      <div
        className={[
          cn.dynamicTabsBlock,
          layout === 'horizontal' && cn.tabHorizontal,
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className={`${cn.dynamicTabsElement} ${elementClass}`}>
          <div className={`${cn.dynamicTabsNavigation} ${tabClass}`}>
            <ul className={`${cn.dynamicTabsList} ${listClassName}`}>
              {showFlowIndicator && (
                <span
                  className={[cn.flowingBackground, bgFlowClass]
                    .filter(Boolean)
                    .join(' ')}
                  style={flowStyle}
                />
              )}
              {tabs.map((tab, index) => (
                <li
                  key={index}
                  ref={(el) => {
                    if (el) tabRefs.current[index] = el;
                  }}
                  className={`${cn.dynamicTabsItem} ${itemClassName}`}
                  onClick={() =>
                    changeTab(index, 'click', triggerOnActiveClick)
                  }
                  onMouseEnter={() =>
                    changeOnHover && changeTab(index, 'hover')
                  }
                >
                  <a
                    className={[
                      cn.dynamicTabsLink,
                      getModuleClass(tab.tabClass),
                      linkClass,
                      activeTab === index && cn.activeLink,
                      activeTab === index && getModuleClass(tab.activeTabClass),
                      activeTab === index && activeClassName,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {tab.icon && (
                      <span className={`${cn.tabIconWrapper} ${tabIconClass}`}>
                        {typeof tab.icon === 'string' ? (
                          <CustomImage
                            src={tab.icon}
                            alt={tab.title}
                            width={30}
                            height={30}
                          />
                        ) : (
                          tab.icon
                        )}
                      </span>
                    )}
                    {ReactHtmlParser(tab.title)}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className={`${cn.dynamicTabsContent} ${contentClass}`}>
          <div className={`${cn.dynamicContentBlock} ${contentBlockClass}`}>
            {tabs.map((tab, index) => (
              <div
                key={index}
                className={getTabClassName(index)}
                style={{
                  display: index === activeTab ? 'block' : 'none',
                }}
              >
                {tab.content}
              </div>
            ))}
          </div>
        </div>
      </div>
      {showIndenter && (
        <ul className={cn.dynamicTabsIndenter}>
          {tabs.map((_, index) => (
            <li
              key={index}
              onClick={() => changeTab(index, 'click', triggerOnActiveClick)}
              className={`${cn.indenterBubble} ${index === activeTab ? cn.activeBubble : ''}`}
            >
              <span />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default DynamicTabs;
